<template>
    <VAppLayout>
        <header class="menu-header" :class="{ 'menu-header--collapsed': collapsed }">
            <div class="menu-header__top">
                <div class="menu-header__heading">
                    <span v-if="venueName" class="menu-header__eyebrow">{{ venueName }}</span>
                    <h1 class="menu-header__title">{{ $t('pages.menu.title') }}</h1>
                </div>
                <div class="menu-header__actions">
                    <button v-if="collapsed && !showFullSearch"
                            type="button"
                            class="menu-header__icon-btn"
                            :aria-label="$t('pages.menu.search')"
                            @click="openMiniSearch">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                            <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" />
                            <path d="M20 20L16.5 16.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                        </svg>
                    </button>
                    <button v-if="hasFavorites"
                            type="button"
                            class="menu-header__icon-btn menu-header__fav"
                            :class="{ 'is-pop': favPop }"
                            :aria-label="$t('pages.menu.favorites.title')"
                            @click="goToFavorites">
                        <IconStar width="20" height="20" />
                    </button>
                    <VAppLocale @update:value="pullFullMenu(true)" />
                </div>
            </div>

            <div class="menu-header__search-wrap" :class="{ 'is-collapsed': !showFullSearch }">
                <div class="menu-header__search">
                    <svg class="menu-header__search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" />
                        <path d="M20 20L16.5 16.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                    <input ref="searchInput"
                           v-model="query"
                           class="menu-header__search-input"
                           :placeholder="$t('pages.menu.search')">
                    <button v-if="query || searchOpen"
                            type="button"
                            class="menu-header__search-clear"
                            aria-label="clear"
                            @click="clearSearch">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                            <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" />
                        </svg>
                    </button>
                </div>
            </div>

            <MenuCategorySelect v-show="!searching"
                                :categories="comCategories"
                                :selectedCategory="selectedCategory"
                                :collapsed="collapsed" />
        </header>

        <MenuHorizontalScroll v-if="comPopular !== null && !searching"
                              :id="comPopular.id"
                              :title="comPopular.name"
                              :items="comPopular.items"
                              ref="popularRef" />
        <VContainer>
            <VAlert
                v-if="shouldShowAlert"
                v-model="alert.show"
                :type="alert.type"
                :text="alertText"
                :dismissible="false"
                :html="alert.html"
            />

            <MenuInfScroll :query="query" @loadedItems="addObserveElements" />
        </VContainer>
    </VAppLayout>
</template>

<script>
import VAppLayout from "@/layouts/VAppLayout.vue";
import VContainer from "@/components/ui/VContainer.vue";
import VAppLocale from "@/components/ui/VAppLocale.vue";
import VAlert from "@/components/ui/VAlert.vue";
import MenuCategorySelect from "./components/MenuCategorySelect.vue";
import MenuInfScroll from "./components/MenuInfScroll.vue";
import MenuHorizontalScroll from "./components/MenuHorizontalScroll.vue";
import IconStar from "@/components/icons/IconStar.vue";
import { isWithinSmartMenuWorkingHours } from "@/utils/scheduleHelper";

export default {
    name: "MenuTabNew",
    components: {
        VAppLayout,
        VContainer,
        VAppLocale,
        VAlert,
        MenuCategorySelect,
        MenuInfScroll,
        MenuHorizontalScroll,
        IconStar,
    },
    data() {
        return {
            observer: null,
            selectedCategory: "",
            firstCategory: null,
            collapsed: false,
            searchOpen: false,
            favPop: false,
            favPopTimer: null,
            query: "",
            alert: {
                show: true,
                type: 'warning',
                text: '',
                html: true
            }
        };
    },
    created() {
        this.observer = new IntersectionObserver(
            this.onElementObserved,
            {
                root: this.$el,
                rootMargin: '-25% 0% -75% 0%',
                threshold: 0,
            }
        );

        // Not force-refetched: load the menu only if it isn't cached yet. Combined
        // with <keep-alive> above, returning to /menu reuses the loaded data and DOM
        // instead of re-fetching + rebuilding every item (and its <img>). A fresh
        // pull still happens on first load and on language change (VAppLocale).
        this.pullFullMenu(false);
    },
    activated() {
        // The document is the scroll container (required for iOS Safari to collapse
        // its toolbars), so listen on window — and only while this kept-alive page
        // is the active one, or scrolling other routes would drive our header state.
        // activated() also fires after the initial mount, so this covers first load.
        window.addEventListener('scroll', this.onScroll, { passive: true });
        // Re-sync the collapsed header with the actual scroll position on return,
        // avoiding a stale-header flash.
        this.onScroll();
    },
    deactivated() {
        window.removeEventListener('scroll', this.onScroll);
    },
    beforeDestroy() {
        this.observer.disconnect();
        clearTimeout(this.favPopTimer);
        window.removeEventListener('scroll', this.onScroll);
    },
    computed: {
        comMenu() {
            return this.$store.getters['menuStore/getMenu'] || [];
        },
        comPopular() {
            return this.$store.getters['menuStore/getPopular'] || null;
        },
        comCategories() {
            const res = [];

            if (this.comPopular !== null) {
                res.push({ id: this.comPopular.id, name: this.comPopular.name });
            }
            const categories = this.comMenu.map(c => ({ id: c.id, name: c.name }));

            return res.concat(categories);
        },
        companyData() {
            return this.$store.getters['getCompanyData'] || {};
        },
        venueName() {
            return this.companyData.locale ? this.companyData.locale.name : '';
        },
        searching() {
            return this.query.trim().length > 0;
        },
        showFullSearch() {
            return !this.collapsed || this.searchOpen || this.searching;
        },
        shouldShowAlert() {
            const locale = this.companyData.locale;
            if (!locale) return false;
            if (locale.pos_terminal_enabled === 0 && !this.$store.getters['getIsModeWMM']) return true;
            if (locale.pos_terminal_enabled && locale.smart_menu_schedule) {
                return !isWithinSmartMenuWorkingHours(locale.smart_menu_schedule);
            }
            return false;
        },
        alertText() {
            const locale = this.companyData.locale;
            if (locale && locale.pos_terminal_enabled === 0) {
                const title = this.$t('alerts.orders_disabled.title');
                const message = this.$t('alerts.orders_disabled.message');
                return `<span style="font-weight:600">${title}</span><br>${message}`;
            }
            const title = this.$t('alerts.outside_working_hours.title');
            const message = this.$t('alerts.outside_working_hours.message');
            return `<span style="font-weight:600">${title}</span><br>${message}`;
        },
        favoritesCount() {
            return this.$store.getters['favoritesStore/getCount'];
        },
        hasFavorites() {
            return this.favoritesCount > 0;
        }
    },
    watch: {
        // Pulse the header star whenever the shortlist grows, so a diner who just
        // starred a dish sees where to find it. Only on growth — removals/clears
        // shouldn't fire the "look here" cue.
        favoritesCount(newVal, oldVal) {
            if (newVal > oldVal) {
                this.pulseFav();
            }
        }
    },
    methods: {
        goToFavorites() {
            this.$router.push({ name: 'menu-favorites' });
        },
        pulseFav() {
            // Restart the animation even on back-to-back adds: clear the class first,
            // then re-add on the next frame so the keyframes replay from the start.
            this.favPop = false;
            clearTimeout(this.favPopTimer);
            this.$nextTick(() => {
                this.favPop = true;
                this.favPopTimer = setTimeout(() => { this.favPop = false; }, 700);
            });
        },
        onScroll() {
            // Hysteresis: collapse and expand at two different thresholds instead
            // of one. Collapsing the header shrinks its height (the eyebrow, the
            // full search bar and the chips padding all disappear), which pulls the
            // scroller's scrollTop back up. With a single threshold that rebound
            // could re-cross it and flip the state again, so a slow scroll parked at
            // the boundary makes the header jump forever. The dead-band between the
            // two thresholds is wider than that rebound, so the state settles once.
            const top = window.scrollY;
            if (!this.collapsed && top > 64) {
                this.collapsed = true;
            } else if (this.collapsed && top < 8) {
                this.collapsed = false;
            }
            if (!this.collapsed) this.searchOpen = false;
        },
        openMiniSearch() {
            this.searchOpen = true;
            this.$nextTick(() => {
                if (this.$refs.searchInput) this.$refs.searchInput.focus();
            });
        },
        clearSearch() {
            this.query = "";
            this.searchOpen = false;
        },
        addObserveElements(elements) {
            if (Array.isArray(elements)) {
                elements.forEach(element => {
                    this.observer.observe(element);
                })
            }
        },
        onElementObserved(entries) {
            entries.forEach(({ target, isIntersecting }) => {
                if (isIntersecting) {
                    this.selectedCategory = target.id;
                }
            });
        },
        pullFullMenu(forceUpdate) {
            this.$store.dispatch('menuStore/GET_FULL_MENU', forceUpdate).then(() => this.afterMenuLoad());
        },
        afterMenuLoad() {
            if (this.firstCategory !== null) {
                this.firstCategory.classList.remove("first-category");
            }
            const observeElements = [];

            if (this.comPopular !== null && this.$refs.popularRef) {
                const el = this.$refs.popularRef.$el;
                observeElements.push(el);
                if (this.firstCategory === null) {
                    this.firstCategory = el;
                }
            }
            this.addObserveElements(observeElements);

            if (this.firstCategory === null && this.comMenu.length !== 0) {
                const escapeId = CSS.escape(this.comMenu[0].id);
                this.firstCategory = this.$el.querySelector(`#${escapeId}`) || null;
            }

            if (this.firstCategory !== null) {
                this.firstCategory.classList.add("first-category");
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.menu-header {
    position: sticky;
    top: 0;
    z-index: 1100;

    box-sizing: border-box;
    padding-top: calc(20px + var(--safe-top, 0px));

    background: var(--header-blur-bg);
    backdrop-filter: blur(26px) saturate(180%);
    -webkit-backdrop-filter: blur(26px) saturate(180%);
    border-bottom: 1px solid var(--border-subtle);
    box-shadow: inset 0 1px 0 var(--header-hairline);
}

.menu-header__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    padding: 4px 16px 6px;
}

.menu-header__heading {
    min-width: 0;
}

.menu-header__eyebrow {
    display: block;

    font-size: 11.5px;
    font-weight: var(--weight-bold);
    line-height: 1.4;
    letter-spacing: 0.05em;
    text-transform: uppercase;

    color: var(--color-primary);

    max-height: 18px;
    opacity: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    transition: max-height var(--dur-base) var(--ease-standard),
                opacity 160ms var(--ease-standard);
}

.menu-header__title {
    margin: 0;

    font-size: 22px;
    font-weight: var(--weight-black);
    line-height: 1.1;
    letter-spacing: -0.02em;

    color: var(--text-primary);

    transition: font-size var(--dur-base) var(--ease-standard);
}

.menu-header--collapsed {
    .menu-header__eyebrow {
        max-height: 0;
        opacity: 0;
    }

    .menu-header__title {
        font-size: 18px;
    }
}

.menu-header__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.menu-header__icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;

    width: 38px;
    height: 38px;

    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-pill);
    background: var(--surface-card);
    color: var(--text-secondary);
    cursor: pointer;
}

.menu-header__fav {
    position: relative;
    color: var(--fav-star);
    -webkit-tap-highlight-color: transparent;

    /* Expanding ring on add — a second "look here" cue beyond the star pop. */
    &::after {
        content: "";
        position: absolute;
        inset: -1px;
        border-radius: inherit;
        border: 2px solid var(--fav-star);
        opacity: 0;
        pointer-events: none;
    }

    &.is-pop {
        animation: fav-pop 0.7s var(--ease-standard);
    }

    &.is-pop::after {
        animation: fav-ring 0.7s var(--ease-standard);
    }
}

@keyframes fav-pop {
    0%   { transform: scale(1) rotate(0); }
    25%  { transform: scale(1.32) rotate(-9deg); }
    45%  { transform: scale(0.9) rotate(6deg); }
    65%  { transform: scale(1.14) rotate(-3deg); }
    100% { transform: scale(1) rotate(0); }
}

@keyframes fav-ring {
    0%   { transform: scale(1); opacity: 0.55; }
    100% { transform: scale(1.85); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
    .menu-header__fav.is-pop,
    .menu-header__fav.is-pop::after {
        animation: none;
    }
}

.menu-header__search-wrap {
    max-height: 66px;
    opacity: 1;
    overflow: hidden;

    padding: 0 16px;

    transition: max-height 240ms var(--ease-standard),
                opacity 180ms var(--ease-standard);

    &.is-collapsed {
        max-height: 0;
        opacity: 0;
    }
}

.menu-header__search {
    display: flex;
    align-items: center;
    gap: 10px;

    margin: 6px 0 10px;
    height: 46px;
    padding: 0 14px;

    border: 1px solid var(--border-subtle);
    border-radius: 14px;
    background: var(--surface-card);

    transition: height var(--dur-base) var(--ease-standard);

    .menu-header--collapsed & {
        height: 42px;
        margin-top: 8px;
    }
}

.menu-header__search-icon {
    flex-shrink: 0;
    color: var(--text-muted);
}

.menu-header__search-input {
    flex: 1;
    min-width: 0;

    border: none;
    outline: none;
    background: transparent;

    font-family: var(--font-sans);
    font-size: 15px;
    font-weight: var(--weight-medium);
    color: var(--text-primary);

    &::placeholder {
        color: var(--text-muted);
    }
}

.menu-header__search-clear {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    width: 24px;
    height: 24px;

    border: none;
    border-radius: var(--radius-pill);
    background: var(--surface-subtle);
    color: var(--text-secondary);
    cursor: pointer;
}

// Disable browser scroll anchoring for the page content. When the sticky header
// collapses it loses ~90px of height (eyebrow, full search bar and chips
// padding all disappear); with anchoring on, Chrome/Firefox/Android rebound
// the scroll offset to keep the content still, and that rebound re-crosses the
// collapse threshold — so a slow scroll parked on the boundary flips the header
// forever. The document is the scroller now; overflow-anchor:none excludes this
// whole subtree from anchor selection, which disables anchoring in practice.
// (iOS Safari has no scroll anchoring, so it was never affected there.)
::v-deep .app_layout-content {
    overflow-anchor: none;
}

::v-deep .first-category {
    scroll-margin-top: 100vh;
}
</style>
